<template>
    <div class="modal fade" 
        :class="this.modalPosition" 
        :id="modalID" 
        :aria-labelledby="theModalLabel" 
        tabindex="-1"
        aria-hidden="true"
        @click.self="close">

        <div class="np-base-dialog modal-dialog modal-lg modal-dialog-centered w-100" :class="modalClass">
                <div class="modal-content shadow">
                    <div class="modal-header border-0 p-4 pb-2">
                        <span class="col-sm-8 d-flex align-middle">
                            <svg class="icon"><use href="@/assets/images/icons/cog.svg#icon"></use></svg>
                            {{ projectData.id ? $t("UI.editProject") : $t("UI.addProject") }}
                        </span>
                        <div class="d-flex justify-content-end flex-grow-1">
                            <button type="button" class="np-btn np-outline me-2" @click="doEditable" v-if="!isEditable">
                                <svg class="icon"><use href="@/assets/images/icons/edit.svg#icon"></use></svg> 
                                {{ $t("UI.edit") }}
                            </button> 
                            <button type="button" class="np-btn np-freeze np-outline me-2" @click="doReadable" v-if="isEditable">
                                <svg><use href="@/assets/images/icons/eye.svg#icon"></use></svg> 
                            </button>                                                         
                            <button type="button" class="np-btn np-outline" @click="close">
                                <svg><use href="@/assets/images/icons/close3.svg#icon"></use></svg> 
                            </button>
                        </div>
                    </div>

                    <div class="modal-body ps-3 pe-3">
                        <div class="row m-0 w-100 h-100" v-if="projectData">
                            <div class="col">
                                <div class="mb-3">
                                    <label class="np-font-m np-f-medium mb-2" for="projectName">{{ $t("UI.project.name") }}</label>
                                    <input type="text" class="form-control np-input" id="projectName" aria-describedby="projectNameHelp"
                                        v-model="projectData.name" :readonly="!isEditable">
                                    <small id="projectNameHelp" class="form-text text-muted"></small>
                                </div>
                                <div class="mb-4">
                                    <label class="np-font-m np-f-medium mb-2" for="projectDescription">{{ $t("UI.project.description") }}</label>
                                    <textarea 
                                        id="projectDescription"
                                        class="form-control np-textarea"
                                        style="height: 100px; resize: none;"
                                        v-model="projectData.description" :readonly="!isEditable" v-if="isEditable">
                                    </textarea>
                                    <div v-else class="np-font-s">
                                        {{ projectData.description }}
                                    </div>
                                </div>
                                <div class="mb-4">
                                    <label class="np-font-m np-f-medium mb-2" for="projectBudgetPlan">{{ $t("UI.project.budget") }}</label>
                                    <input type="number" class="form-control np-input" id="projectBudgetPlan" aria-describedby="budgetPlanHelp"
                                        v-model="budgetPlan" :readonly="!isEditable">
                                    <small id="budgetPlanHelp" class="form-text text-muted"></small>
                                </div>      
                                <div class="row">
                                    <div class="col">
                                        <div class="mb-4">
                                            <label class="np-font-m np-f-medium mb-2" for="taskDeadline">{{ $t("UI.project.dateStart") }}</label>
                                            <div>
                                                <flat-pickr 
                                                    v-if="isMounted"
                                                    v-model="datePlannedStart" 
                                                    :disabled="!isEditable"
                                                    :placeholder="this.$t('UI.selectDate')"
                                                    :config="{
                                                        enableTime: false,
                                                        dateFormat: 'U',
                                                        altInput: true,
                                                        altFormat: 'd.m.Y H:i',
                                                        time_24hr: true                                      
                                                    }"
                                                >
                                                </flat-pickr>                                        
                                            </div>
                                        </div>                                         
                                    </div>

                                    <div class="col">
                                        <div class="mb-4">
                                            <label class="np-font-m np-f-medium mb-2" for="taskDeadline">{{ $t("UI.project.dateFinish") }}</label>
                                            <div>
                                                <flat-pickr 
                                                    v-if="isMounted"
                                                    v-model="datePlannedEnd" 
                                                    :disabled="!isEditable"
                                                    :placeholder="this.$t('UI.selectDate')"
                                                    :config="{
                                                        enableTime: false,
                                                        dateFormat: 'U',
                                                        altInput: true,
                                                        altFormat: 'd.m.Y H:i',
                                                        time_24hr: true                                      
                                                    }"
                                                >
                                                </flat-pickr>                                        
                                            </div>
                                        </div>                                        
                                    </div>
                                </div>  
                                <div class="mb-4">
                                    <label class="np-font-m np-f-medium mb-2" for="exampleInputLogin">{{ $t("UI.project.projectAuthor") }}</label>
                                    <CobaltProfile 
                                        :firstName="projectData.employee_info?.initiator.first_name" 
                                        :lastName="projectData.employee_info?.initiator.last_name" 
                                        size="np-avatar-small icon"
                                    />
                                </div>                                                                                               
                            </div>
                            <div class="col">
                                <div class="mb-4">
                                    <div class="np-font-m np-f-medium mb-2">{{ $t("UI.project.projectDirector") }}</div>
                                    <Multiselect v-if="isEditable"
                                        v-model="selectedManagers" 
                                        :placeholder="this.$t('UI.select')"
                                        :multiple="true"
                                        :options="members" 
                                        :class="['cobalt-task__multiselect']"
                                        track-by="id"                              
                                        selectLabel=""
                                        label="first_name"
                                        selectedLabel=""
                                        deselectLabel="&#x2715;"
                                        open-direction="bottom"
                                        @remove="removeManager"
                                    >
                                        <template #selection="{ values, search, isOpen }">
                                            <div class="multiselect__single cobalt-profile-list__wrapper" v-show="!isOpen">
                                                <div class="cobalt-profile-list__item" v-for="item in values">
                                                    <CobaltProfile 
                                                        :firstName="item.first_name" 
                                                        :lastName="item.last_name" 
                                                        :short="values.length > 1 ? true : false"
                                                        size="np-avatar-small"
                                                    />
                                                </div>
                                            </div>
                                        </template>                            
                                        <template #option="props">
                                            <span class="np-font-s">
                                                {{ props.option.first_name + ' ' + props.option.last_name }}
                                            </span>
                                        </template>
                                    </Multiselect>  
                                    <div v-else class="np-group flex-wrap">
                                        <template v-for="item in selectedManagers">
                                            <div class="me-2">
                                                <CobaltProfile 
                                                    :firstName="item.first_name" 
                                                    :lastName="item.last_name" 
                                                    :short="false"
                                                    size="np-avatar-small"
                                                />
                                            </div>
                                        </template>
                                    </div>
                                    <div v-if="selectedManagers.length == 0 && !isEditable" class="np-font-s">
                                        {{ $t("UI.project.notAssigned") }}
                                    </div>
                                </div>
                                <div class="mb-4">
                                    <div class="np-font-m np-f-medium mb-2">{{ $t("UI.project.projectControllers") }}</div>
                                    <Multiselect v-if="isEditable"
                                        v-model="selectedControllers" 
                                        :placeholder="this.$t('UI.select')"
                                        :multiple="true"
                                        :options="members" 
                                        :class="['cobalt-task__multiselect']"
                                        track-by="id"                              
                                        selectLabel=""
                                        label="first_name"
                                        selectedLabel=""
                                        deselectLabel="&#x2715;"
                                        open-direction="bottom"
                                        @remove="removeController"
                                    >
                                        <template #selection="{ values, search, isOpen }">
                                            <div class="multiselect__single cobalt-profile-list__wrapper" v-show="!isOpen">
                                                <div class="cobalt-profile-list__item" v-for="item in values">
                                                    <CobaltProfile 
                                                        :firstName="item.first_name" 
                                                        :lastName="item.last_name" 
                                                        :short="values.length > 1 ? true : false"
                                                        size="np-avatar-small"
                                                    />
                                                </div>
                                            </div>
                                        </template>                            
                                        <template #option="props">
                                            <span class="np-font-s">
                                                {{ props.option.first_name + ' ' + props.option.last_name }}
                                            </span>
                                        </template>
                                    </Multiselect>  
                                    <div v-else class="np-group flex-wrap">
                                        <template v-for="item in selectedControllers">
                                            <div class="me-2">
                                                <CobaltProfile 
                                                    :firstName="item.first_name" 
                                                    :lastName="item.last_name" 
                                                    :short="false"
                                                    size="np-avatar-small"
                                                />
                                            </div>
                                        </template>
                                    </div>
                                    <div v-if="selectedControllers.length == 0 && !isEditable" class="np-font-s">
                                        {{ $t("UI.project.notAssigneds") }}
                                    </div>
                                </div>                                    
                                <div class="np-group np-group-column mb-4">
                                    <div class="np-font-m np-f-medium mb-2">{{ $t("UI.project.projectMembers") }}</div>
                                    <div class="np-group np-group-column np-mt-3 np-flex-1" style="max-height: 268px;">
                                        <PerfectScrollbar>
                                            <ul class="np-list">
                                                <li role="button" v-for="em in members" :key="em.id"> 
                                                    <div class="np-group align-items-center p-2">
                                                        <CobaltProfile 
                                                            :firstName="em.first_name" 
                                                            :lastName="em.last_name" 
                                                            :avatar="em.imageFile" 
                                                            size="cobalt-profile icon"
                                                            short
                                                        />                                   
                                                        <div class="np-group np-group-column ps-3 flex-grow-1">
                                                            <div class="np-font-m pb-1">{{ em.first_name }} {{ em.last_name }}</div>
                                                            <div class="np-subheader3">{{ em.position?.name }}</div>
                                                        </div>
                                                        <div class="np-group align-items-start">
                                                            <button class="np-btn np-btn-sm np-outline me-2">
                                                                <svg><use href="@/assets/images/icons/key.svg#icon"></use></svg>
                                                            </button>
                                                            <button class="np-btn np-btn-sm np-outline">
                                                                <svg><use href="@/assets/images/icons/dots-vertical.svg#icon"></use></svg>
                                                            </button>                                                            
                                                        </div>                                                         
                                                    </div>            
                                                </li>                                                                                                                                                                                                                                                                                                                               
                                            </ul>
                                        </PerfectScrollbar>   
                                    </div>
                                </div>                                 
                            </div>
                        </div>                            
                    </div>

                    <div class="modal-footer p-3" v-if="isEditable">
                        <button type="button" class="np-btn np-danger px-4 me-1"
                            data-bs-dismiss="modal" :disabled="isLoading">{{ $t("UI.close") }}</button>
                        <button type="button" class="np-btn np-success-filled px-4" @click="updateProject()" :disabled="isLoading">
                            <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
                            {{ $t("UI.save") }}
                        </button>
                    </div>                    
                </div>
        </div>
    </div>
</template>

<script>
import CoverUploader from "@/components/elements/CoverUploader.vue";
import DatePicker from 'vue-datepicker-next';
import 'vue-datepicker-next/index.css';
import { mapGetters, mapActions } from 'vuex'
import { Modal } from "bootstrap"
import { ref } from 'vue'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import CobaltProfile from "@/components/dashboard/parts/CobaltProfile.vue"
import Multiselect from 'vue-multiselect'

export default {
    components: {
        flatPickr,
        CoverUploader,
        DatePicker,
        CobaltProfile,
        Multiselect
    },
    props: {
        modalID: {
            type: String,
            required: true
        },        
        modalSize: {
            type: String,
            default: ''
        },
        modalPosition: {
            type: String,
            default: ''
        }          
    }, 
    mounted() {
        this.isMounted = true
    },    
    data() {
        return {  
            isMounted: false,
            emptyProject: {
                id: 0,
                company_id: 0,
                name: '',
                description: '',
                date_planned_start: null,
                date_planned_end: null,
                date_create: 0,
                date_update: 0,
                budget: {
                    budget_plan: 0             
                }
                
            },
            projectData: {},
            members: [],
            isNewProject: false,           
            theModal: null,
            theModalLabel: null,
            isLoading: false,
            isEditable: false,
            controllers: [],
            managers: [],
        }
    },    
    computed: {
        datePlannedStart: {
            get() {
                return this.projectData.date_planned_start ? this.projectData.date_planned_start + "" : null
            },
            set(option) {
                this.projectData.date_planned_start = option ? parseInt(option) : null
            }
        },
        datePlannedEnd: {
            get() {
                return this.projectData.date_planned_end ? this.projectData.date_planned_end + "": null
            },
            set(option) {
                this.projectData.date_planned_end = option ? parseInt(option) : null
            }
        },
        budgetPlan: {
            get() {
                return this.projectData.budget?.budget_plan ? this.projectData.budget.budget_plan : null
            },
            set(option) {
                this.projectData.budget.budget_plan = parseInt(option)
            }
        },
        selectedControllers: {
            get() {
                return this.controllers
            },
            set(option) {
                if (Array.isArray(option) && option.length > 0) {
                    const updatedControllers = [...option]
                    this.controllers = updatedControllers
                    this.doUpdateController(true, option[option.length - 1])
                }
            }            
        },
        selectedManagers: {
            get() {
                return this.managers
            },
            set(option) {
                if (Array.isArray(option) && option.length > 0) {
                    const updatedManagers = [...option]
                    this.managers = updatedManagers
                    this.doUpdateManager(true, option[option.length - 1])
                }
            }            
        }                               
    },
    mounted: function () {
        var IdModal = '#' + this.$props.modalID
        this.theModalLabel = this.modalID + "Label"
        this.theModal = new Modal(IdModal, {backdrop: true})
    },           
    methods: {
        ...mapActions('project', ['info', 'listUsers', 'saveProject', 'listControllers', 'assignController', 'detachController', 'listManagers', 'assignManager', 'detachManager']),
        ...mapActions('dashboard', ['projectInfo']),

        async fetchControllers(id) {
            const temp = await this.listControllers(id)
            this.controllers = []
            if (temp) {
                temp.forEach(item => {
                    this.controllers.push(item.user)
                })  
            }
        },
        async fetchManagers(id) {
            const temp = await this.listManagers(id)
            this.managers = []
            if (temp) {
                temp.forEach(item => {
                    this.managers.push(item.user)
                })  
            }
        },        
        async shows(companyId, projectId) {
            this.isMounted = true

            if (!projectId) {
                this.projectData = Object.assign({}, this.emptyProject)
            } else {
                this.projectData = await this.info(projectId)
                this.projectData["uploadImage"] = null
            }
            
            if (companyId) {
                this.projectData.company_id = companyId
            }

            this.theModal.show()
            
            this.isLoading = true
            
            if (projectId) {
                this.members = await this.listUsers(projectId)
                await this.fetchControllers(projectId)
                await this.fetchManagers(projectId)
            } else {
                this.isEditable = true
            }            

            this.isLoading = false
        },
        hide() {
            this.members = []
            this.isEditable = false
            this.theModal.hide()
        },
        async close() {
            this.members = []
            this.isEditable = false
            this.$emit('closeModal')
            this.hide()
        },
        async updateProject() {
            this.isLoading = true

            let project = await this.saveProject(this.projectData)
            
            await this.$emit('refresh', project)
            this.hide()

            this.isLoading = false
        }, 
        doEditable() {
            this.isEditable = true
        },
        doReadable() {
            this.isEditable = false
        },
        async doUpdateController(operation, controller) {
            if (operation) {
                await this.assignController({
                    projectId: this.projectData.id,
                    userId: controller.id
                })
            } else {
                await this.detachController({
                    projectId: this.projectData.id,
                    userId: controller.id
                })                
            }
        },
        async removeController(option, id) {
            const updatedControllers = this.controllers.filter(item => item.id !== option.id)
            this.controllers = updatedControllers
            this.doUpdateController(false, option)
        },
        async doUpdateManager(operation, manager) {
            if (operation) {
                await this.assignManager({
                    projectId: this.projectData.id,
                    userId: manager.id
                })
            } else {
                await this.detachManager({
                    projectId: this.projectData.id,
                    userId: manager.id
                })                
            }
        },
        async removeManager(option, id) {
            const updatedManagers = this.managers.filter(item => item.id !== option.id)
            this.managers = updatedManagers
            this.doUpdateManager(false, option)
        }                
    }
}
</script>

<style>
</style>